(function(angular, _) {
    'use strict';

    angular.module('wtApp.dataService')
        .factory('WtPathViewMode', ['$injector', function($injector) {
            const $q = $injector.get('$q');
            const Monads = $injector.get('Monads');
            const DSCONST = $injector.get('DSCONST');
            const viewModePath = $injector.get('viewModePath');
            const WtObservable = $injector.get('WtObservable');

            /**
             * @name WtPathViewMode
             * @description Factory responsible for the analysis path view mode management
             */
            return class WtPathViewMode {
                /**
                 * @ngdoc property
                 * @name WtPathViewMode.ACTIONS
                 * @description A map of actions that can be emitted by the viewModes "changes" observable
                 * @returns {Object}
                 * @static
                 */
                static get ACTIONS() {
                    return {
                        /**
                         * Triggered when the pathLength property of the path view mode configuration is being changed
                         */
                        VIEW_MODES_PATH_LENGTH_UPDATE: 'VIEW_MODES_PATH_LENGTH_UPDATE',

                        /**
                         * Triggered when the reverse property of the path view mode configuration is being changed
                         */
                        VIEW_MODES_PATH_REVERSE_UPDATE: 'VIEW_MODES_PATH_REVERSE_UPDATE',

                        /**
                         * Triggered when the distinct property of the path view mode configuration is being changed
                         */
                        VIEW_MODES_PATH_DISTINCT_UPDATE: 'VIEW_MODES_PATH_DISTINCT_UPDATE',

                        /**
                         * Triggered when the chartState property of the path view mode configuration is being changed
                         */
                        VIEW_MODES_PATH_CHART_STATE_UPDATE: 'VIEW_MODES_PATH_CHART_STATE_UPDATE',

                        /**
                         * Triggered when the dimensionContext property of the path view mode configuration is being changed
                         */
                        VIEW_MODES_PATH_DIMENSION_CONTEXT_UPDATE: 'VIEW_MODES_PATH_DIMENSION_CONTEXT_UPDATE',

                        /**
                         * Triggered when the metricArrangeType property of the path view mode configuration is being changed
                         */
                        VIEW_MODES_PATH_METRIC_ARRANGE_TYPE_UPDATE: 'VIEW_MODES_PATH_METRIC_ARRANGE_TYPE_UPDATE',

                        /**
                         * Generic event informing about any type of change performed on the path view mode configuration
                         */
                        VIEW_MODES_PATH_UPDATE: 'VIEW_MODES_PATH_UPDATE'
                    };
                }

                /**
                 * @ngdoc property
                 * @name WtPathViewMode.VIEW_MODE_NAME
                 * @description Returns the view mode name - i.e. as it can be traced in the viewMode config.
                 * @returns {string}
                 * @static
                 */
                static get VIEW_MODE_NAME() {
                    return viewModePath.VIEW_MODE_NAME;
                }

                /**
                 * @ngdoc property
                 * @name WtPathViewMode.STATE_PROPS
                 * @description List of path viewMode properties that are related to its view state.
                 * @returns {Array<string>}
                 * @static
                 */
                static get STATE_PROPS() {
                    return [
                        'chartState'
                    ];
                }

                /**
                 * @ngdoc property
                 * @name WtPathViewMode.STATE_PATHS
                 * @description List of path viewMode configuration property paths that are related to its view state.
                 * @returns {Array<string>}
                 * @static
                 */
                static get STATE_PATHS() {
                    return _.map(WtPathViewMode.STATE_PROPS, (prop) => `${WtPathViewMode.VIEW_MODE_NAME}.${prop}`);
                }

                /**
                 * @ngdoc method
                 * @name WtPathViewMode.isNoneContextDerivative
                 * @description Determines if a VISITOR dimensionContext derived from a dimension with NONE context.
                 * @param   {Array<Object>} columns List of analysis columns
                 * @param   {string}  dimensionContext
                 * @returns {boolean}
                 * @static
                 */
                static isNoneContextDerivative(columns, dimensionContext) {
                    const dimension = viewModePath.getPathCompatibleDimension(columns);
                    return _.get(dimension, 'context') === DSCONST.CONTEXTS.NONE && dimensionContext === DSCONST.CONTEXTS.VISITOR;
                }

                /**
                 * @ngdoc method
                 * @name WtPathViewMode.isNoneContextDerivative
                 * @description Returns the context to be used to retrieve a mapped metric arrangeType.
                 * @param   {Array<Object>} columns List of analysis columns
                 * @param   {string}  dimensionContext
                 * @returns {string}
                 * @static
                 */
                static getMetricMappingContext(columns, dimensionContext) {
                    return WtPathViewMode.isNoneContextDerivative(columns, dimensionContext) ? DSCONST.CONTEXTS.NONE : dimensionContext;
                }

                /**
                 * @ngdoc method
                 * @name WtPathViewMode.getDefaultMetricArrangeType
                 * @description Returns the default metric arrangeType that corresponds to a given set of columns and a dimensionContext.
                 * @param   {Array<Object>} columns List of analysis columns
                 * @param   {string}  dimensionContext
                 * @returns {string}
                 * @static
                 */
                static getDefaultMetricArrangeType(columns, dimensionContext) {
                    const metricMappingContext = WtPathViewMode.getMetricMappingContext(columns, dimensionContext);

                    return _.get(viewModePath.DIMENSION_CONTEXT_TO_METRIC_ARRANGE_TYPE, `${metricMappingContext}`);
                }

                /**
                 * @ngdoc property
                 * @name WtPathViewMode#pristineConfig
                 * @description Exposes the pristine viewMode configuration object to the outer world
                 * @returns {Object}
                 */
                get pristineConfig() {
                    return this.pristineData_;
                }

                /**
                 * @ngdoc property
                 * @name WtPathViewMode#config
                 * @description Exposes the viewMode configuration object to the outer world
                 * @returns {Object}
                 */
                get config() {
                    return this.data_;
                }

                /**
                 * @ngdoc property
                 * @name WtPathViewMode#actions
                 * @description Exposes the possible actions map to be used by the columns changes consumers
                 * @returns {Object}
                 */
                get actions() {
                    return WtPathViewMode.ACTIONS;
                }

                /**
                 * @ngdoc property
                 * @name WtPathViewMode#pathLengths
                 * @description Exposes the range of possible pathLength values.
                 * @returns {Array<number>}
                 */
                get pathLengths() {
                    return _.range(viewModePath.MIN_PATH_LENGTH, viewModePath.MAX_PATH_LENGTH + 1);
                }

                /**
                 * @ngdoc property
                 * @name WtPathViewMode#distincts
                 * @description Exposes the list of possible distinct.
                 * @returns {Array<boolean>}
                 */
                get distincts() {
                    return [true, false];
                }

                /**
                 * @ngdoc property
                 * @name WtPathViewMode#reverses
                 * @description Exposes the list of possible reverse.
                 * @returns {Array<boolean>}
                 */
                get reverses() {
                    return [true, false];
                }

                /**
                 * @ngdoc property
                 * @name WtPathViewMode#metric
                 * @description Exposes the metric's monad of the path view mode.
                 * @returns {Object}
                 */
                get metric() {
                    return viewModePath.getPathMetric(this.config);
                }

                /**
                 * @ngdoc property
                 * @name WtPathViewMode#metricAlias
                 * @description Exposes the alias of the path view mode metric query.
                 * @returns {string}
                 */
                get metricAlias() {
                    return viewModePath.getMetricAlias(this.config);
                }

                /**
                 * @ngdoc property
                 * @name WtPathViewMode#hasOrigin
                 * @description Determines if the path view mode derives from an existing source config or if it was
                 * generated on the fly based on default props.
                 * @returns {boolean}
                 */
                get hasOrigin() {
                    return !_.isNil(this.configOrigin_);
                }

                /**
                 * @ngdoc property
                 * @name WtPathViewMode#hasOrigin
                 * @description Determines if the path view mode derives from an source config that was automagically corrected.
                 * @returns {boolean}
                 */
                get hasCorruptOrigin() {
                    if (!this.hasOrigin) {
                        return false;
                    }

                    const stateProps = WtPathViewMode.STATE_PROPS;

                    const origin = _.omit(this.configOrigin_, stateProps);

                    const pristine = _.omit(this.pristineConfig, stateProps);

                    return !_.isEqual(origin, pristine);
                }

                /**
                 * @ngdoc method
                 * @name WtPathViewMode#constructor
                 * @description WtPathViewMode constructor
                 * @param {Object} config The viewMode configuration
                 * @param {Object} configOrigin The origin of the viewMode configuration
                 */
                constructor(config, configOrigin) {
                    /**
                     * Holds the source the viewMode config originated from
                     * @type {Object}
                     * @private
                     */
                    this.configOrigin_ = _.cloneDeep(configOrigin);

                    /**
                     * Holds the original data
                     * @type {Object}
                     * @private
                     */
                    this.pristineData_ = Object.freeze(_.cloneDeep(config));

                    /**
                     * Holds the current data
                     * @type {Object}
                     * @private
                     */
                    this.data_ = _.cloneDeep(config);

                    /**
                     * The observer object that can be used to push changes into the observable
                     * @example
                     *  - Notify the listeners about a new value: this.changesEmitter_.next('value');
                     *  - Notify the listeners about an error: this.changesEmitter_.error(new Error('Something went wrong'))
                     *  - Notify the listeners about the completion: this.changesEmitter_.complete()
                     * @type {null|Object}
                     * @private
                     */
                    this.changesEmitter_ = null;

                    /**
                     * The observable object that can be subscribed to listen to the changes on the viewModes instance
                     * @example
                     *  const subscription = viewModes.changes.subscribe((action) => {
                     *      const type = action.type;
                     *      const payload = action.payload;
                     *      // React to the viewModes change
                     *  });
                     *
                     *  // Cleanup method should be invoked when the listener is destroyed or does not need the updates
                     *  subscription.unsubscribe();
                     * @type {WtObservable}
                     */
                    this.changes = new WtObservable((observer) => {
                        this.changesEmitter_ = observer;
                    });
                }

                /**
                 * @ngdoc method
                 * @name WtPathViewMode#getColumns
                 * @description Returns a list of columns for the path viewMode containing only the first dimension and
                 * a column generated from the metricArrangeType
                 * @param   {WtAnalysisColumns} columns
                 * @returns {Array<Object>}
                 */
                getColumns(columns) {
                    const dimension = columns.getFirstDimension();
                    const metricAlias = this.metricAlias;
                    return _.map(columns.getCompleteConfiguration_([dimension, metricAlias]), (column) => {
                        return column.alias === metricAlias ? _.set(column, 'sortDirection', 'DESCENDING') : _.omit(column, 'sortDirection');
                    });
                }

                /**
                 * @ngdoc method
                 * @name WtPathViewMode#canApplyPath
                 * @description Determines if the path configuration can be converted to a query.
                 * @param   {Array<Object>} columns
                 * @returns {boolean}
                 */
                canApplyPath(columns) {
                    return viewModePath.canApplyPath(this.config, columns);
                }

                /**
                 * @ngdoc method
                 * @name WtPathViewMode#resolveDimensionContexts
                 * @description Resolves the possible dimension contexts based on a list of analysis columns.
                 * @param   {Array<Object>} columns
                 * @returns {Promise}
                 */
                resolveDimensionContexts(columns) {
                    return new $q((resolve) => resolve(viewModePath.getDimensionContexts(columns)));
                }

                /**
                 * @ngdoc method
                 * @name WtPathViewMode#resolveMetrics
                 * @description Resolves all metrics that are usable path query generation.
                 * @param   {Array<Object>} columns
                 * @param   {Array<string>} dimensionContexts
                 * @returns {Promise}
                 */
                resolveMetrics(columns, dimensionContexts) {
                    const dimension = viewModePath.getPathCompatibleDimension(columns);
                    const metrics = _.filter(Monads.metrics(), (metric) => viewModePath.isValidMetricForPath(metric, dimension, dimensionContexts));
                    return new $q((resolve) => resolve(metrics));
                }

                /**
                 * @ngdoc method
                 * @name WtPathViewMode#isDimensionContextCompatible
                 * @description Determines compatibility of a dimension context against a metric configuration object.
                 * @param   {Object}  metric
                 * @param   {string}  dimensionContext
                 * @returns {boolean}
                 */
                isDimensionContextCompatible(metric, dimensionContext) {
                    const possibleContexts = _.get(metric, 'possibleContexts', []);
                    return metric && _.isEmpty(possibleContexts) ? dimensionContext === DSCONST.CONTEXTS.VISITOR : _.includes(possibleContexts, dimensionContext);
                }

                /**
                 * @ngdoc method
                 * @name WtPathViewMode#setMetric
                 * @description Sets the metricArrangeType value in the path configuration reseting if necessary the dimensionContext
                 * if necessary.
                 * @param   {Object} metric
                 * @param   {Array<Object>} columns
                 * @param   {List<string>} dimensionContexts
                 * @returns {Promise}
                 */
                setMetric(metric, columns, dimensionContexts) {
                    const dimension = viewModePath.getPathCompatibleDimension(columns);

                    return new $q((resolve, reject) => {
                        if (!viewModePath.isValidMetricForPath(metric, dimension, dimensionContexts)) {
                            return reject(`Metric ${_.get(metric, 'title')} cannot be used in path configuration.`);
                        }

                        const dimensionContext = this.setDimensionContextByMetric_(metric, dimensionContexts);

                        const metricArrangeType = _.get(metric, 'arrangeType', '');

                        this.set('metricArrangeType', metricArrangeType);

                        return resolve({dimensionContext, metric});
                    });
                }

                /**
                 * @ngdoc method
                 * @name WtPathViewMode#setDimensionContext
                 * @description Sets the dimensionContext value in the path configuration reseting if necessary the metricArrangeType
                 * if necessary.
                 * @param   {string} dimensionContext
                 * @param   {Array<string>} dimensionContexts
                 * @param   {Array<Object>} metrics
                 * @param   {Array<Object>} columns
                 * @returns {Promise}
                 */
                setDimensionContext(dimensionContext, dimensionContexts, metrics, columns) {
                    return new $q((resolve, reject) => {
                        if (!_.includes(dimensionContexts, dimensionContext)) {
                            return reject(`Context ${dimensionContext} cannot be used in path configuration.`);
                        }

                        const metric = this.setMetricArrangeTypeByDimensionContext_(metrics, dimensionContext, columns);

                        this.set('dimensionContext', dimensionContext);

                        return resolve({dimensionContext, metric});
                    });
                }

                /**
                 * @ngdoc method
                 * @name WtPathViewMode#initMetricAndDimensionContext
                 * @description Initializes the metric arrangeType and the context of the path configuration instance by:
                 * 1. Validating the metric against the analysis columns for query building.
                 * 2. If the metric is valid the path configuration context is re-evaluated and possibly reset.
                 * 3. If the metric is invalid, the context is validated and possibly reset to the first valid context.
                 * 4. With a valid path configuration context the metric is re-evaluated and possibly reset to a valid fallback.
                 * @param   {Array<Object>} columns List of analysis columns
                 * @param   {Array<string>} dimensionContexts List of contexts derived from the dimension of an analysis
                 * @param   {Array<Object>} metrics List of path compatible metric monad objects
                 * @returns {Promise}
                 */
                initMetricAndDimensionContext(columns, dimensionContexts, metrics) {
                    const metric = this.getPathMetric_(metrics);
                    const dimension = viewModePath.getPathCompatibleDimension(columns);

                    if (viewModePath.isValidMetricForPath(metric, dimension, dimensionContexts)) {
                        // Sets metric and re-evaluates and possibly resets the path configuration context.
                        return this.setMetric(metric, columns, dimensionContexts);
                    }

                    let dimensionContext = this.get('dimensionContext');

                    if (!viewModePath.isValidDimensionContext(dimensionContext, dimensionContexts)) {
                        dimensionContext = _.first(dimensionContexts);
                    }

                    // Sets contect and re-evaluates and possibly resets the path configuration metric.
                    return this.setDimensionContext(dimensionContext, dimensionContexts, metrics, columns);
                }

                /**
                 * @ngdoc method
                 * @name WtPathViewMode#scalePathLength
                 * @description Increases the pathLength property of the view mode configuration instance by a given step;
                 * making sure not to exceed the max defined path length limit.
                 * @param   {number} step
                 * @returns {WtPathViewMode}
                 */
                scalePathLength(step = 5) {
                    const pathLength = _.min([this.get('pathLength') + step, viewModePath.MAX_PATH_LENGTH]);

                    return this.set('pathLength', pathLength);
                }

                /**
                 * @ngdoc method
                 * @name WtPathViewMode#toJSON
                 * @description Converts the instance into a serializable object
                 * @returns {Object}
                 */
                toJSON() {
                    return this.data_;
                }

                /**
                 * @ngdoc method
                 * @name WtPathViewMode#toString
                 * @description Stringify the instance
                 * @returns {string}
                 */
                toString() {
                    return JSON.stringify(
                        this.toJSON()
                    );
                }

                /**
                 * @ngdoc method
                 * @name WtPathViewMode#dispatchAction
                 * @description Emits an event in the viewModes changes observable
                 * @param {string} type The event name
                 * @param {Object=} payload The payload of the event
                 * @returns {WtPathViewMode}
                 */
                dispatchAction(type, payload = {}) {
                    if (type) {
                        this.changesEmitter_.next({type, payload});
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtPathViewMode#isPristine
                 * @description Checks if the viewMode configuration was changed since it was initialized
                 * @returns {boolean}
                 */
                isPristine() {
                    return _.isEqual(this.pristineData_, this.data_);
                }

                /**
                 * @ngdoc method
                 * @name WtPathViewMode#get
                 * @description Analysis viewMode configuration accessor
                 * @param {string} iPath The path within the analysis viewMode configuration (see lodash _.get())
                 * @returns {*}
                 */
                get(iPath) {
                    const path = iPath ? String(iPath) : null;
                    return path ? _.get(this.data_, path) : null;
                }

                /**
                 * @ngdoc method
                 * @name WtPathViewMode#set
                 * @description Path viewMode configuration setter
                 * @param {string} path_ The path within the path viewMode (see lodash _.set())
                 * @param {*} currentValue The value to be set
                 * @returns {WtPathViewMode}
                 */
                set(path_, currentValue) {
                    const path = path_ ? String(path_) : null;
                    const previousValue = this.get(path);

                    if (!path || _.isEqual(previousValue, currentValue)) {
                        return this;
                    }

                    _.set(this.data_, path, currentValue);

                    const payload = {path, previousValue, currentValue};

                    switch (path) {
                        case 'pathLength':
                            return this.dispatchAction(this.actions.VIEW_MODES_PATH_LENGTH_UPDATE, payload);
                        case 'reverse':
                            this.dispatchAction(this.actions.VIEW_MODES_PATH_REVERSE_UPDATE, payload);

                            return this.set('chartState', []);
                        case 'distinct':
                            return this.dispatchAction(this.actions.VIEW_MODES_PATH_DISTINCT_UPDATE, payload);
                        case 'chartState':
                            return this.dispatchAction(this.actions.VIEW_MODES_PATH_CHART_STATE_UPDATE, payload);
                        case 'dimensionContext':
                            return this.dispatchAction(this.actions.VIEW_MODES_PATH_DIMENSION_CONTEXT_UPDATE, payload);
                        case 'metricArrangeType':
                            return this.dispatchAction(this.actions.VIEW_MODES_PATH_METRIC_ARRANGE_TYPE_UPDATE, payload);
                        default:
                            return this.dispatchAction(this.actions.VIEW_MODES_PATH_UPDATE, payload);
                    }
                }

                /**
                 * @ngdoc method
                 * @name WtPathViewMode#destroy
                 * @description Method invoked before the instance is destroyed to release the resources and perform the cleanup.
                 */
                destroy() {
                    if (this.changesEmitter_) {
                        this.changesEmitter_.complete();
                    }
                }

                /**
                 * @ngdoc method
                 * @name WtPathViewMode#getPathMetric_
                 * @description Retrieves the metric monad configuration of the path metric arrangeType given a list of meric monads.
                 * @param  {Array<Object>} metrics List of metric monads
                 * @returns {Object|null}
                 * @private
                 */
                getPathMetric_(metrics) {
                    return _.find(metrics, {arrangeType: this.get('metricArrangeType')}) || null;
                }

                /**
                 * @ngdoc method
                 * @name WtPathViewMode#setDimensionContextByMetric_
                 * @description Sets the context of the path configuration like so:
                 * 1. Valid path configuration context; no action is taken
                 * 2. Invalid path configuration context; its being reset to the first metric compatible context
                 * @param   {Object} metric Metric monad object
                 * @param   {Array<list>} dimensionContexts List of possible contexts derived from the analysis' dimension
                 * @returns {string}
                 * @private
                 */
                setDimensionContextByMetric_(metric, dimensionContexts) {
                    let dimensionContext = this.get('dimensionContext');

                    if (!this.isDimensionContextCompatible(metric, dimensionContext) || !_.includes(dimensionContexts, dimensionContext)) {
                        dimensionContext = _.chain(dimensionContexts)
                            .filter((context) => this.isDimensionContextCompatible(metric, context))
                            .first()
                            .valueOf() || '';

                        this.set('dimensionContext', dimensionContext);
                    }

                    return dimensionContext;
                }

                /**
                 * @ngdoc method
                 * @name WtPathViewMode#getDefaultMetricByContext_
                 * @description Returns the metric monad based on default map of contexts and metric arrangeTypes, making sure to:
                 * 1. Identify NONE derived VISITOR path configuration context
                 * 2. Validate the retrieved default metric arrangeType against the path configuration context
                 * @param   {Array<Object>} metrics List of path compatible metric monad objects
                 * @param   {string} dimensionContext
                 * @param   {Array<Object>} columns List of analysis columns
                 * @returns {Object|null}
                 * @private
                 */
                getDefaultMetricByContext_(metrics, dimensionContext, columns) {
                    const arrangeType = WtPathViewMode.getDefaultMetricArrangeType(columns, dimensionContext);

                    const defaultMetric = _.find(metrics, {arrangeType});

                    return defaultMetric && this.isDimensionContextCompatible(defaultMetric, dimensionContext) ? defaultMetric : null;
                }

                /**
                 * @ngdoc method
                 * @name WtPathViewMode#setMetricArrangeTypeByDimensionContext_
                 * @description Sets the metric arrangeType of the path configuration like so:
                 * 1. Valid path configuration metric; no action is taken
                 * 2. Invalid path configuration metric; metric arrange type is reset to a default if applicable
                 * @param   {Array<Object>} metrics List of path compatible metric monad objects
                 * @param   {string} dimensionContext
                 * @param   {Array<Object>} columns List of analysis columns
                 * @returns {Object|null}
                 * @private
                 */
                setMetricArrangeTypeByDimensionContext_(metrics, dimensionContext, columns) {
                    const metric = this.getPathMetric_(metrics);

                    if (this.isDimensionContextCompatible(metric, dimensionContext)) {
                        return metric;
                    }

                    // If the metric arrangeType of the current path configuration is found invalid due to:
                    // 1. Analysis configuration changes
                    // 2. User derived context change
                    // an attempt is made to provide a fallback default metric.
                    const defaultMetric = this.getDefaultMetricByContext_(metrics, dimensionContext, columns);

                    const metricArrangeType = _.get(defaultMetric, 'arrangeType', '');

                    this.set('metricArrangeType', metricArrangeType);

                    return defaultMetric;
                }
            };
        }]);
})(window.angular, window._);
